<template>
  <div class="approval-page">
    <!-- Header -->
    <div class="viva-page-header">
      <div class="viva-header-content">
        <img src="/viva/viva_logo.svg" alt="Viva" class="viva-logo" />
        <h1 class="viva-page-title">ACCOUNT APPROVAL</h1>
      </div>
    </div>

    <div class="viva-container" style="display: block; padding: 0 30px;">
      <!-- Navigation -->
      <div class="nav-links">
        <router-link to="/qa" class="nav-link">QA Page</router-link>
        <span class="nav-separator">|</span>
        <span class="nav-link nav-active">Account Approval</span>
      </div>

      <!-- Create Instructor -->
      <div class="actions-section">
        <button @click="showCreateModal = true" class="viva-btn viva-btn-green" style="font-size: 16px; padding: 10px 24px;">
          <i class="fa fa-plus"></i> Create Instructor or Admin Account
        </button>
      </div>

      <!-- Pending Accounts Section -->
      <div class="section">
        <h3 class="section-title">PENDING ACCOUNTS</h3>

        <div v-if="pendingLoading" class="loading-container">
          <i class="fa fa-spinner fa-spin"></i> Loading pending accounts...
        </div>

        <div v-else-if="pendingError" class="empty-state">
          <p>{{ pendingError }}</p>
          <button @click="fetchPendingAccounts" class="btn btn-primary btn-sm" style="margin-top: 10px;">Try Again</button>
        </div>

        <div v-else-if="pendingAccounts.length === 0" class="empty-state">
          <p>No pending accounts.</p>
        </div>

        <div v-else class="pending-list">
          <div class="pending-header">
            <span class="col-name">Name</span>
            <span class="col-email">Email</span>
            <span class="col-org">Organization</span>
            <span class="col-role">Role</span>
            <span class="col-source">Source</span>
            <span class="col-action">Action</span>
          </div>
          <div v-for="account in pendingAccounts" :key="account.id" class="pending-row">
            <span class="col-name">{{ account.first_name }} {{ account.last_name }}</span>
            <span class="col-email">{{ account.email }}</span>
            <span class="col-org">{{ account.organization_name || '-' }}</span>
            <span class="col-role">
              <span class="role-badge" :class="account.role_type === 'admin' ? 'role-admin' : 'role-instructor'">
                {{ account.role_type }}
              </span>
            </span>
            <span class="col-source">
              <span class="source-badge" :class="account.creation_endpoint === 'viva_admin_added_instructor' ? 'source-admin' : 'source-self'">
                {{ account.creation_endpoint === 'viva_admin_added_instructor' ? 'Admin Added' : 'Self Signup' }}
              </span>
            </span>
            <span class="col-action">
              <template v-if="account.role_type === 'admin' && !account.organization_name">
                <button
                  @click="openAssignSchoolModal(account)"
                  class="btn btn-warning btn-sm"
                  style="margin-bottom: 4px; display: block;"
                >
                  Assign School
                </button>
                <div style="font-size: 11px; color: #856404;">Required before approval</div>
              </template>
              <button
                v-else
                @click="approveAccount(account)"
                class="btn btn-success btn-sm"
                :disabled="approveLoading === account.id"
              >
                {{ approveLoading === account.id ? 'Approving...' : 'Approve' }}
              </button>
            </span>
          </div>
        </div>
      </div>

      <!-- All Accounts Section -->
      <div class="section">
        <h3 class="section-title">ALL ACCOUNTS</h3>

        <div v-if="allLoading" class="loading-container">
          <i class="fa fa-spinner fa-spin"></i> Loading all accounts...
        </div>

        <div v-else-if="allError" class="empty-state">
          <p>{{ allError }}</p>
          <button @click="fetchAllAccounts" class="btn btn-primary btn-sm" style="margin-top: 10px;">Try Again</button>
        </div>

        <div v-else>
          <FilterSortTable
            title="Instructor & Admin Accounts"
            :tableStructure="tableStructure"
            :itemheight="47"
            :interviews="allAccounts"
            :initialOrderBy="'last_name'"
            :initialSortOrder="1"
            @toggle-active="handleToggleActive"
            @set-school="openAssignSchoolModal"
          />
        </div>
      </div>
    </div>

    <!-- Create Instructor Modal -->
    <div v-if="showCreateModal" class="viva-modal-backdrop" @click="showCreateModal = false">
      <div class="viva-modal-content" @click.stop>
        <div class="viva-modal-header">
          <h2>Create Account</h2>
          <button type="button" class="close" @click="showCreateModal = false" aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="viva-modal-body">
          <div class="form-group">
            <input type="text" v-model="createForm.firstName" placeholder="First Name" class="form-control">
          </div>
          <div class="form-group">
            <input type="text" v-model="createForm.lastName" placeholder="Last Name" class="form-control">
          </div>
          <div class="form-group">
            <input type="email" v-model="createForm.email" placeholder="Email" class="form-control">
          </div>

          <!-- Role Selection -->
          <div class="form-group">
            <label>Role:</label>
            <div style="display: flex; gap: 15px; margin-top: 5px;">
              <label style="cursor: pointer;">
                <input type="radio" v-model="createForm.roleType" value="instructor"> Instructor
              </label>
              <label style="cursor: pointer;">
                <input type="radio" v-model="createForm.roleType" value="admin"> School Administrator
              </label>
            </div>
          </div>

          <!-- School Search -->
          <div class="form-group">
            <label>School:</label>
            <div class="search-input-wrapper">
              <input
                type="text"
                v-model="schoolSearchQuery"
                @input="handleSchoolSearch"
                placeholder="Search schools by name or CEEB..."
                class="form-control"
                :disabled="selectedSchool"
              >
            </div>
            <div v-if="schoolSearchLoading" style="padding: 8px 0; color: #918E8A;">
              <i class="fa fa-spinner fa-spin"></i> Searching...
            </div>
            <div v-if="!schoolSearchLoading && schoolResults.length > 0" class="school-results-dropdown-modal">
              <div
                v-for="(school, index) in schoolResults"
                :key="school.ceeb_code || index"
                class="school-result-item-modal"
                @click="selectSchool(school)"
              >
                {{ school.name }} <span v-if="school.location">({{ school.location }})</span>
              </div>
            </div>
            <div v-if="selectedSchool" class="selected-school-modal">
              {{ selectedSchool.name }}
              <button type="button" @click="clearSchoolSelection" class="btn btn-xs btn-default" style="margin-left:10px;">Change</button>
            </div>
            <div v-if="!selectedSchool && schoolResults.length === 0 && createForm.roleType !== 'admin'" style="margin-top: 8px;">
              <input
                type="text"
                v-model="schoolNameFallback"
                placeholder="Can't find your school? Enter school name here."
                class="form-control"
                :disabled="createLoading"
                maxlength="255"
              >
            </div>
            <div v-if="!selectedSchool && createForm.roleType === 'admin'" style="margin-top: 8px; color: #856404; font-size: 13px;">
              Administrator accounts require a school selected from the list above.
            </div>
          </div>

          <div v-if="createError" class="alert alert-danger" style="margin-top: 10px;">
            {{ createError }}
          </div>
          <div v-if="createSuccess" class="alert alert-success" style="margin-top: 10px;">
            {{ createSuccess }}
          </div>
        </div>
        <div class="viva-modal-footer">
          <button
            @click="createInstructor"
            class="btn btn-primary"
            :disabled="!createFormValid || createLoading"
          >
            {{ createLoading ? 'Creating...' : (createForm.roleType === 'admin' ? 'Create Administrator' : 'Create Instructor') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Assign School Modal -->
    <div v-if="showAssignSchoolModal" class="viva-modal-backdrop" @click="closeAssignSchoolModal">
      <div class="viva-modal-content" @click.stop>
        <div class="viva-modal-header">
          <h2>Assign School</h2>
          <button type="button" class="close" @click="closeAssignSchoolModal" aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="viva-modal-body">
          <p style="margin-bottom: 15px;">
            Assigning a school for <strong>{{ assignSchoolUser ? (assignSchoolUser.first_name + ' ' + assignSchoolUser.last_name) : '' }}</strong>
            ({{ assignSchoolUser ? assignSchoolUser.email : '' }}).
          </p>

          <div class="form-group">
            <label>School:</label>
            <div class="search-input-wrapper">
              <input
                type="text"
                v-model="assignSchoolSearchQuery"
                @input="handleAssignSchoolSearch"
                placeholder="Search schools by name or CEEB..."
                class="form-control"
                :disabled="assignSchoolSelectedSchool"
              >
            </div>
            <div v-if="assignSchoolSearchLoading" style="padding: 8px 0; color: #918E8A;">
              <i class="fa fa-spinner fa-spin"></i> Searching...
            </div>
            <div v-if="!assignSchoolSearchLoading && assignSchoolResults.length > 0" class="school-results-dropdown-modal">
              <div
                v-for="(school, index) in assignSchoolResults"
                :key="school.ceeb_code || index"
                class="school-result-item-modal"
                @click="selectAssignSchool(school)"
              >
                {{ school.name }} <span v-if="school.location">({{ school.location }})</span>
              </div>
            </div>
            <div v-if="assignSchoolSelectedSchool" class="selected-school-modal">
              {{ assignSchoolSelectedSchool.name }}
              <button type="button" @click="clearAssignSchoolSelection" class="btn btn-xs btn-default" style="margin-left:10px;">Change</button>
            </div>
          </div>

          <div v-if="assignSchoolError" class="alert alert-danger" style="margin-top: 10px;">
            {{ assignSchoolError }}
          </div>
          <div v-if="assignSchoolSuccess" class="alert alert-success" style="margin-top: 10px;">
            {{ assignSchoolSuccess }}
          </div>
        </div>
        <div class="viva-modal-footer">
          <button
            @click="assignSchool"
            class="btn btn-primary"
            :disabled="!assignSchoolSelectedSchool || assignSchoolLoading || !!assignSchoolSuccess"
          >
            {{ assignSchoolLoading ? 'Saving...' : 'Save' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from '../utils/apiClient.js'
import FilterSortTable from '@/components/FilterSortTable.vue'

export default {
  name: 'Approval',
  components: {
    FilterSortTable
  },
  data() {
    return {
      // Pending accounts
      pendingLoading: true,
      pendingAccounts: [],
      pendingError: null,
      approveLoading: null,

      // All accounts
      allLoading: true,
      allAccounts: [],
      allError: null,

      // Create instructor modal
      showCreateModal: false,
      createForm: {
        firstName: '',
        lastName: '',
        email: '',
        roleType: 'instructor'
      },
      createError: null,
      createSuccess: null,
      createLoading: false,

      // School search for create modal
      schoolSearchQuery: '',
      schoolResults: [],
      selectedSchool: null,
      schoolNameFallback: '',
      searchTimeout: null,
      schoolSearchLoading: false,

      // Assign school modal (for accounts missing a school)
      showAssignSchoolModal: false,
      assignSchoolUser: null,
      assignSchoolSearchQuery: '',
      assignSchoolResults: [],
      assignSchoolSelectedSchool: null,
      assignSchoolSearchTimeout: null,
      assignSchoolSearchLoading: false,
      assignSchoolLoading: false,
      assignSchoolError: null,
      assignSchoolSuccess: null,

      // Table structure for FilterSortTable
      tableStructure: [
        {
          ref: "toggle-active",
          title: "",
          icon: "glyphicon glyphicon-off",
          filtersort: "none",
          filter: "",
          style: "max-width:50px;min-width:50px;",
          cellType: "emitButton",
        },
        {
          ref: "last_name",
          title: "Last Name",
          filtersort: "text",
          filter: "",
          style: "max-width:150px;min-width:120px;",
        },
        {
          ref: "first_name",
          title: "First Name",
          filtersort: "text",
          filter: "",
          style: "max-width:150px;min-width:120px;",
        },
        {
          ref: "email",
          title: "Email",
          filtersort: "text",
          filter: "",
          style: "max-width:220px;min-width:180px;",
        },
        {
          ref: "emailPersonal",
          title: "Alt Email",
          filtersort: "text",
          filter: "",
          style: "max-width:200px;min-width:150px;",
        },
        {
          ref: "organization_name",
          title: "Organization",
          filtersort: "text",
          filter: "",
          style: "max-width:200px;min-width:150px;",
          cellType: "value-or-button",
          buttonTitle: "Set School",
          emitEvent: "set-school",
        },
        {
          ref: "role_type",
          title: "Role",
          filtersort: "text",
          filter: "",
          style: "max-width:150px;min-width:100px;",
        },
        {
          ref: "course_count",
          title: "Courses",
          filtersort: "text",
          filter: "",
          style: "max-width:80px;min-width:80px;",
        },
        {
          ref: "assignment_count",
          title: "Assignments",
          filtersort: "text",
          filter: "",
          style: "max-width:100px;min-width:100px;",
        },
        {
          ref: "active_status",
          title: "Status",
          filtersort: "dropdown",
          filter: "",
          style: "max-width:100px;min-width:100px;",
          cellType: "options",
          options: [
            { val: "active", title: "Active" },
            { val: "inactive", title: "Inactive" }
          ]
        }
      ]
    }
  },
  computed: {
    createFormValid() {
      return this.createForm.firstName.trim() && this.createForm.lastName.trim() && this.createForm.email.trim() && this.createForm.roleType &&
        (this.createForm.roleType !== 'admin' || !!this.selectedSchool)
    }
  },
  mounted() {
    this.fetchPendingAccounts()
    this.fetchAllAccounts()
  },
  methods: {
    async fetchPendingAccounts() {
      this.pendingLoading = true
      this.pendingError = null
      try {
        const response = await apiClient.get('/api/viva/qa/pending_accounts/')
        this.pendingAccounts = response.data.accounts || []
      } catch (err) {
        console.error('Error fetching pending accounts:', err)
        this.pendingError = 'Failed to load pending accounts.'
      } finally {
        this.pendingLoading = false
      }
    },

    async fetchAllAccounts() {
      this.allLoading = true
      this.allError = null
      try {
        const response = await apiClient.get('/api/viva/qa/all_instructor_accounts/')
        this.allAccounts = (response.data.accounts || []).map(a => ({
          ...a,
          active_status: a.r_active ? 'active' : 'inactive'
        }))
        // Force FilterSortTable to recalculate column widths after data renders
        this.$nextTick(() => {
          setTimeout(() => {
            window.dispatchEvent(new Event('resize'))
          }, 100)
        })
      } catch (err) {
        console.error('Error fetching all accounts:', err)
        this.allError = 'Failed to load accounts.'
      } finally {
        this.allLoading = false
      }
    },

    async approveAccount(account) {
      this.approveLoading = account.id
      try {
        await apiClient.post('/api/viva/qa/approve_account/', { user_id: account.id })
        // Remove from pending list
        this.pendingAccounts = this.pendingAccounts.filter(a => a.id !== account.id)
        // Refresh all accounts
        this.fetchAllAccounts()
      } catch (err) {
        console.error('Error approving account:', err)
        alert('Failed to approve account. Please try again.')
      } finally {
        this.approveLoading = null
      }
    },

    async handleToggleActive(account) {
      try {
        const response = await apiClient.post('/api/viva/qa/toggle_account_active/', { user_id: account.id })
        // Update locally
        const idx = this.allAccounts.findIndex(a => a.id === account.id)
        if (idx !== -1) {
          this.allAccounts[idx].r_active = response.data.r_active
          this.allAccounts[idx].active_status = response.data.r_active ? 'active' : 'inactive'
          // Force reactivity
          this.allAccounts = [...this.allAccounts]
        }
        // Also refresh pending in case state changed
        this.fetchPendingAccounts()
      } catch (err) {
        console.error('Error toggling account:', err)
        alert('Failed to toggle account. Please try again.')
      }
    },

    async createInstructor() {
      this.createLoading = true
      this.createError = null
      this.createSuccess = null
      try {
        const payload = {
          first_name: this.createForm.firstName.trim(),
          last_name: this.createForm.lastName.trim(),
          email: this.createForm.email.trim(),
          role_type: this.createForm.roleType
        }
        if (this.selectedSchool && this.selectedSchool.ceeb_code) {
          payload.school_ceeb = this.selectedSchool.ceeb_code
        } else if (this.schoolNameFallback.trim()) {
          payload.school_name = this.schoolNameFallback.trim()
        }
        const response = await apiClient.post('/api/viva/qa/create_instructor/', payload)
        this.createSuccess = response.data.message
        this.createForm = { firstName: '', lastName: '', email: '', roleType: 'instructor' }
        this.schoolNameFallback = ''
        this.clearSchoolSelection()
        this.fetchPendingAccounts()
        this.fetchAllAccounts()
      } catch (err) {
        console.error('Error creating instructor:', err)
        this.createError = err.response?.data?.error || 'Failed to create instructor.'
      } finally {
        this.createLoading = false
      }
    },

    // School search methods
    handleSchoolSearch(event) {
      const query = event.target.value
      this.schoolSearchQuery = query

      if (this.searchTimeout) clearTimeout(this.searchTimeout)

      if (!query) {
        this.schoolResults = []
        this.schoolSearchLoading = false
        return
      }

      this.schoolSearchLoading = true
      this.searchTimeout = setTimeout(() => {
        this.performSchoolSearch(query.trim())
      }, 1000)
    },

    async performSchoolSearch(query) {
      try {
        this.schoolSearchLoading = true
        const isCeebCode = /^\d{6}$/.test(query)
        let response
        if (isCeebCode) {
          response = await apiClient.post('/api/viva/validate_ceeb/', { ceeb: query })
          this.schoolResults = response.data.status === 200 && response.data.hs ? [response.data.hs] : []
        } else {
          response = await apiClient.get('/api/viva/search_schools/', { params: { query } })
          this.schoolResults = response.data.status === 200 ? (response.data.hs_list || []) : []
        }
      } catch (error) {
        console.error('School search error:', error)
        this.schoolResults = []
      } finally {
        this.schoolSearchLoading = false
      }
    },

    selectSchool(school) {
      this.selectedSchool = school
      this.schoolResults = []
      this.schoolSearchQuery = ''
    },

    clearSchoolSelection() {
      this.selectedSchool = null
      this.schoolSearchQuery = ''
      this.schoolResults = []
      this.schoolNameFallback = ''
    },

    // Assign school modal methods
    openAssignSchoolModal(account) {
      this.assignSchoolUser = account
      this.assignSchoolSearchQuery = ''
      this.assignSchoolResults = []
      this.assignSchoolSelectedSchool = null
      this.assignSchoolError = null
      this.assignSchoolSuccess = null
      this.showAssignSchoolModal = true
    },

    closeAssignSchoolModal() {
      this.showAssignSchoolModal = false
      this.assignSchoolUser = null
      this.assignSchoolSelectedSchool = null
      this.assignSchoolSearchQuery = ''
      this.assignSchoolResults = []
      this.assignSchoolError = null
      this.assignSchoolSuccess = null
      if (this.assignSchoolSearchTimeout) {
        clearTimeout(this.assignSchoolSearchTimeout)
        this.assignSchoolSearchTimeout = null
      }
    },

    handleAssignSchoolSearch(event) {
      const query = event.target.value
      this.assignSchoolSearchQuery = query

      if (this.assignSchoolSearchTimeout) clearTimeout(this.assignSchoolSearchTimeout)

      if (!query) {
        this.assignSchoolResults = []
        this.assignSchoolSearchLoading = false
        return
      }

      this.assignSchoolSearchLoading = true
      this.assignSchoolSearchTimeout = setTimeout(() => {
        this.performAssignSchoolSearch(query.trim())
      }, 1000)
    },

    async performAssignSchoolSearch(query) {
      try {
        this.assignSchoolSearchLoading = true
        const isCeebCode = /^\d{6}$/.test(query)
        let response
        if (isCeebCode) {
          response = await apiClient.post('/api/viva/validate_ceeb/', { ceeb: query })
          this.assignSchoolResults = response.data.status === 200 && response.data.hs ? [response.data.hs] : []
        } else {
          response = await apiClient.get('/api/viva/search_schools/', { params: { query } })
          this.assignSchoolResults = response.data.status === 200 ? (response.data.hs_list || []) : []
        }
      } catch (error) {
        console.error('School search error:', error)
        this.assignSchoolResults = []
      } finally {
        this.assignSchoolSearchLoading = false
      }
    },

    selectAssignSchool(school) {
      this.assignSchoolSelectedSchool = school
      this.assignSchoolResults = []
      this.assignSchoolSearchQuery = ''
    },

    clearAssignSchoolSelection() {
      this.assignSchoolSelectedSchool = null
      this.assignSchoolSearchQuery = ''
      this.assignSchoolResults = []
    },

    async assignSchool() {
      if (!this.assignSchoolSelectedSchool || !this.assignSchoolUser) return
      this.assignSchoolLoading = true
      this.assignSchoolError = null
      this.assignSchoolSuccess = null
      try {
        const response = await apiClient.post('/api/viva/qa/set_user_department/', {
          user_id: this.assignSchoolUser.id,
          school_ceeb: this.assignSchoolSelectedSchool.ceeb_code,
        })
        // Save is done — clear loading state before showing success or refreshing the table,
        // so the button doesn't say "Saving..." while the All Accounts list refreshes.
        this.assignSchoolLoading = false
        this.assignSchoolSuccess = response.data.message || 'School assigned.'
        // Refresh both lists so the pending admin row shows Approve once a school is linked.
        this.fetchAllAccounts()
        this.fetchPendingAccounts()
        // Brief pause so the user sees the success message, then close.
        setTimeout(() => this.closeAssignSchoolModal(), 800)
      } catch (err) {
        console.error('Error assigning school:', err)
        this.assignSchoolError = err.response?.data?.error || 'Failed to assign school.'
        this.assignSchoolLoading = false
      }
    }
  }
}
</script>

<style scoped>
@import '../styles/viva-classes.css';

.approval-page {
  background-color: var(--viva-cream);
  min-height: 100vh;
}

.viva-container {
  display: block !important;
  align-items: unset !important;
  justify-content: unset !important;
  padding: 0 30px !important;
}

/* Navigation */
.nav-links {
  margin-top: 10px;
  margin-bottom: 20px;
  font-size: 20px;
  margin-left: 15px;
}

.nav-link {
  color: var(--viva-green);
  text-decoration: none;
  font-weight: 600;
  cursor: pointer;
}

.nav-link:hover {
  color: var(--viva-orange);
}

.nav-active {
  color: var(--viva-orange);
  cursor: default;
}

.nav-separator {
  margin: 0 12px;
  color: var(--viva-grey);
}

/* Actions */
.actions-section {
  margin-top: 40px;
  margin-bottom: 30px;
}

/* Sections */
.section {
  margin-bottom: 40px;
}



.section-title {
  color: var(--viva-dark);
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 15px;
}

.loading-container {
  text-align: center;
  padding: 30px;
  color: var(--viva-grey);
}

.empty-state {
  text-align: center;
  padding: 20px;
  background: var(--viva-white);
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

/* Pending list */
.pending-header, .pending-row {
  display: flex;
  align-items: center;
  padding: 10px 15px;
  gap: 10px;
}

.pending-header {
  background: var(--viva-green);
  color: var(--viva-white);
  font-weight: 600;
  border-radius: 8px 8px 0 0;
  font-size: 14px;
}

.pending-row {
  background: var(--viva-white);
  border: 1px solid #e0e0e0;
  border-top: none;
  font-size: 14px;
}

.pending-row:last-child {
  border-radius: 0 0 8px 8px;
}

.col-name { flex: 2; }
.col-email { flex: 3; }
.col-org { flex: 2; }
.col-role { flex: 1; }
.col-source { flex: 1.5; }
.col-action { flex: 1; text-align: center; }

/* Badges */
.role-badge {
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
}

.role-admin {
  background: #e3d5ff;
  color: #5b21b6;
}

.role-instructor {
  background: #d1fae5;
  color: #065f46;
}

.source-badge {
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
}

.source-self {
  background: #d1fae5;
  color: #065f46;
}

.source-admin {
  background: #dbeafe;
  color: #1e40af;
}

/* School search in modal */
.school-results-dropdown-modal {
  border: 1px solid #ccc;
  border-radius: 4px;
  max-height: 200px;
  overflow-y: auto;
  background: white;
  margin-top: 5px;
}

.school-result-item-modal {
  padding: 8px 12px;
  cursor: pointer;
  border-bottom: 1px solid #eee;
}

.school-result-item-modal:hover {
  background: #f5f5f5;
}

.selected-school-modal {
  margin-top: 8px;
  padding: 8px 12px;
  background: #f0f9f0;
  border: 1px solid var(--viva-green);
  border-radius: 4px;
}

.form-group {
  margin-bottom: 15px;
}

@media (max-width: 767px) {
  .pending-header, .pending-row {
    flex-wrap: wrap;
  }
}
</style>

<style>
.approval-page .section .col-md-12 {
  padding-left: 0 !important;
  padding-right: 0 !important;
}
.approval-page .section .row {
  margin-left: 0 !important;
  margin-right: 0 !important;
}
.approval-page .section .col-md-7 {
  padding-left: 0 !important;
}

/* Status badges in FilterSortTable */
.approval-page .active {
  color: #155724;
  background: #d4edda;
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  display: inline-block;
}
.approval-page .inactive {
  color: #721c24;
  background: #f8d7da;
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  display: inline-block;
}
</style>
